<script>
import { apiSecond } from '@/boot/axios'
import { convertObjectToFormData } from '@/formData'
import { alertNcaNotRunning, loadFile } from '@/helpers'

import Region from '@/components/components/Inputs/region'
import Fish from '@/components/components/Inputs/fish'
import AqEmpty from '@/components/aqua/AqEmpty.vue'
import AqField from '@/components/aqua/AqField.vue'
import AqKpi from '@/components/aqua/AqKpi.vue'
import AqPageHeader from '@/components/aqua/AqPageHeader.vue'
import AqStatus from '@/components/aqua/AqStatus.vue'
import ReportManageModal from '@/components/reporting-management/ReportManageModal.vue'
import ReportManageErrorModal from '@/components/reporting-management/ReportManageErrorModal.vue'

import {
	CHAPTERS,
	OBJECT_KEYS,
	OBLIGATION_STATUSES,
	PERCENT_KEYS,
	aquaApiPrefix,
	emptyObligations,
	emptyRow,
	groupedRowFields,
	isEmptyValue,
	rowErrors,
	visibleRowFields,
} from './aquaForms'
import { loadAquaPrefill, loadObjectOptions } from './aquaPrefill'
import AquaObjectInput from './AquaObjectInput.vue'
import AquaSelect from './AquaSelect.vue'

const FIRST_YEAR = 2020

const HEADER_REQUIRED = ['region_id', 'report_year', 'oked_main', 'address', 'phone', 'email', 'executor_name', 'head_full_name']
const HEADER_KEYS = [...HEADER_REQUIRED, 'oked_secondary', 'bank_account']

export default {
	name: 'AquaReportPage',
	components: {
		Region,
		Fish,
		AqEmpty,
		AqField,
		AqKpi,
		AqPageHeader,
		AqStatus,
		AquaSelect,
		AquaObjectInput,
		ReportManageModal,
		ReportManageErrorModal,
	},
	props: {
		scope: {
			type: String,
			default: 'subject',
		},
		statusPage: {
			type: String,
			default: 'create',
		},
	},
	data() {
		return {
			report: null,
			rowKey: 0,
			model: this.emptyModel(),
			document: null,
			returnComment: '',
			signAction: null,
			submitted: false,
			serverErrors: {},
			chapters: CHAPTERS,
			obligationStatuses: OBLIGATION_STATUSES,
			percentKeys: PERCENT_KEYS,
			autofill: [],
			loadError: null,
			objectOptions: [],
			objectKeys: OBJECT_KEYS,
			collapsedRows: [],
			errorMessage: { status: null, text: null },
			message: { status: null, text: null },
		}
	},
	computed: {
		form() {
			return Number(this.$route.params.form)
		},
		isReadonly() {
			return this.statusPage === 'show'
		},
		apiPrefix() {
			return aquaApiPrefix(this.scope, this.form)
		},
		listPath() {
			const base = this.scope === 'go' ? 'reporting-analytics/aqua' : `reporting/${this.scope === 'tir' ? 'aqua-tir' : 'aqua'}`
			return `/${this.$i18n.locale}/account/${base}/${this.form}`
		},
		pageTitle() {
			if (this.statusPage === 'edit') return this.$t('headers.report_editing')
			if (this.statusPage === 'create') return this.$t('headers.report_adding')
			return this.$t('reporting.title')
		},
		userRegions() {
			const regions = this.$store.state.userObject?.regions || []
			return regions.map((region) => ({ ...region, id: region.region_id }))
		},
		statusValue() {
			return this.report?.status?.value || null
		},
		canSign() {
			return (
				this.scope === 'subject' &&
				this.isReadonly &&
				this.report?.is_editable &&
				this.$store.getters.userPermission(['change_reporting-aqua'])
			)
		},
		canReview() {
			return (
				this.scope === 'tir' &&
				this.statusValue === 'subject_signed' &&
				this.$store.getters.userPermission(['change_reporting-aqua-tir'])
			)
		},
		rowErrorList() {
			if (!this.submitted) return this.model.rows.map(() => ({}))
			return this.model.rows.map((row) => rowErrors(this.form, row))
		},
		headerErrors() {
			if (!this.submitted) return {}
			const errors = {}
			const required = this.form === 1 ? [...HEADER_REQUIRED, 'bank_account'] : HEADER_REQUIRED
			required.forEach((key) => {
				if (isEmptyValue(this.model[key])) errors[key] = true
			})
			return errors
		},
		hasClientErrors() {
			return (
				Object.keys(this.headerErrors).length > 0 ||
				this.rowErrorList.some((errors) => Object.keys(errors).length > 0)
			)
		},
		yearOptions() {
			const years = []
			for (let year = new Date().getFullYear(); year >= FIRST_YEAR; year--) years.push({ value: year, text: year })
			return years
		},
		headerTitle() {
			if (this.statusPage === 'create') return this.$t('aqua.ui.new_report')
			if (this.statusPage === 'edit') return this.$t('aqua.ui.edit_report', { year: this.model.report_year })
			return this.$t('aqua.ui.report_for_year', { year: this.model.report_year })
		},
		statusLabel() {
			if (this.statusValue === 'canceled_by_tir') return this.$t('aqua.reporting.on_revision')
			return this.statusValue ? this.$t(`aqua.status.${this.statusValue}`) : ''
		},
		requiredHeaderKeys() {
			return this.form === 1 ? [...HEADER_REQUIRED, 'bank_account'] : HEADER_REQUIRED
		},
		completion() {
			const header = this.requiredHeaderKeys.map((key) => !isEmptyValue(this.model[key]))
			const rows = this.model.rows.flatMap((row) => this.rowRequiredState(row))
			const all = [...header, ...rows]
			return { filled: all.filter(Boolean).length, total: all.length }
		},
		completionPercent() {
			return this.completion.total ? Math.round((this.completion.filled / this.completion.total) * 100) : 0
		},
		errorCount() {
			if (!this.submitted) return 0
			const rowErrors = this.rowErrorList.reduce((sum, errors) => sum + Object.keys(errors).length, 0)
			return Object.keys(this.headerErrors).length + rowErrors
		},
		fishTitles() {
			return Object.fromEntries((this.$store.getters._getFishes || []).map((fish) => [fish.id, fish.title]))
		},
		autofilledKeys() {
			return this.autofill.flatMap((source) => source.keys)
		},
		percentTotals() {
			const totals = this.report?.totals || {}
			return this.percentKeys.filter((key) => key in totals).map((key) => ({ key, value: totals[key] }))
		},
	},
	methods: {
		emptyModel() {
			return {
				region_id: Number(this.$route.query.region_id) || null,
				report_year: Number(this.$route.query.report_year) || new Date().getFullYear() - 1,
				oked_main: '',
				oked_secondary: '',
				address: '',
				phone: '',
				email: '',
				executor_name: '',
				head_full_name: '',
				bank_account: '',
				rows: [],
			}
		},
		fieldLabel(key) {
			return this.$t(`aqua.fields.${key}`)
		},
		visibleFields(row) {
			return visibleRowFields(this.form, row)
		},
		groupedFields(row) {
			return groupedRowFields(this.form, row)
		},
		obligationLabel(row, index) {
			return this.$t(`aqua.obligations.${row.chapter}.${index}`)
		},
		hasError(path, local) {
			return Boolean(local) || Boolean(this.serverErrors[path])
		},
		newRow() {
			this.rowKey += 1
			return { ...emptyRow(this.form), _key: this.rowKey }
		},
		addRow() {
			this.model.rows.push(this.newRow())
		},
		removeRow(index) {
			this.model.rows.splice(index, 1)
		},
		onChapterChange(row, value) {
			row.chapter = value
			if (this.form === 5) row.obligations = emptyObligations(value)
		},
		onDocumentChange(event) {
			this.document = event.target.files[0] || null
		},
		fillModel(report) {
			const model = this.emptyModel()
			HEADER_KEYS.forEach((key) => {
				if (key in report) model[key] = report[key] ?? ''
			})
			model.region_id = report.region?.id || null
			model.report_year = report.report_year
			model.rows = report.rows.map((row) => {
				const prepared = this.newRow()
				Object.keys(prepared).forEach((key) => {
					if (key in row) prepared[key] = row[key]
				})
				return prepared
			})
			this.model = model
		},
		payload() {
			const payload = { ...this.model }
			if (this.form !== 1) delete payload.bank_account
			payload.rows = this.model.rows.map((row) => {
				const prepared = {}
				this.visibleFields(row).forEach((field) => {
					prepared[field.key] = row[field.key]
				})
				return prepared
			})
			if (this.form === 4 && this.document) payload.document = this.document
			return payload
		},
		async loadReport() {
			if (this.statusPage === 'create') {
				this.addRow()
				if (this.scope === 'subject') this.applyPrefill()
				return
			}
			try {
				const { data } = await apiSecond.get(`${this.apiPrefix}/${this.$route.params.id}`)
				this.report = data.data
				this.fillModel(this.report)
				if (this.statusPage === 'edit' && this.scope === 'subject') this.objectOptions = await loadObjectOptions()
			} catch (error) {
				const status = error?.response?.status
				if (status === 401) return this.checkError(error)
				this.loadError = status === 403 || status === 404 ? 'denied' : 'failed'
			}
		},
		async applyPrefill() {
			const { header, rows, sources, objectOptions } = await loadAquaPrefill(this.form, this.model.region_id)
			this.objectOptions = objectOptions
			Object.keys(header).forEach((key) => {
				if (isEmptyValue(this.model[key])) this.model[key] = header[key]
			})
			const blank = emptyRow(this.form)
			const untouched =
				this.model.rows.length === 1 && Object.keys(blank).every((key) => blank[key] === this.model.rows[0][key])
			if (rows.length && untouched) this.model.rows = rows.map((row) => ({ ...this.newRow(), ...row }))
			if (!rows.length && untouched && this.form !== 3 && objectOptions.length === 1) {
				OBJECT_KEYS.filter((key) => key in this.model.rows[0]).forEach((key) => {
					this.model.rows[0][key] = objectOptions[0]
				})
			}
			this.autofill = sources
		},
		isAutofilled(key) {
			return !this.isReadonly && this.autofilledKeys.includes(key)
		},
		rowRequiredState(row) {
			return this.visibleFields(row)
				.filter((field) => field.required)
				.map((field) => {
					if (field.type === 'obligations') return Array.isArray(row.obligations) && row.obligations.every(Boolean)
					return !isEmptyValue(row[field.key])
				})
		},
		rowProgress(row) {
			const state = this.rowRequiredState(row)
			return { filled: state.filter(Boolean).length, total: state.length }
		},
		rowTitle(row, index) {
			const chapter = row.chapter ? this.$t(`aqua.chapters.${row.chapter}`) : ''
			return this.fishTitles[row.fish_id] || chapter || this.$t('aqua.ui.row_untitled', { n: index + 1 })
		},
		rowSubtitle(row) {
			return row.fishery_object_name || row.water_object_name || ''
		},
		isCollapsed(row) {
			return this.collapsedRows.includes(row._key)
		},
		toggleRow(row) {
			this.collapsedRows = this.isCollapsed(row)
				? this.collapsedRows.filter((key) => key !== row._key)
				: [...this.collapsedRows, row._key]
		},
		displayValue(value) {
			return isEmptyValue(value) ? '—' : value
		},
		scrollToSection(id) {
			document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
		},
		focusFirstError() {
			this.collapsedRows = []
			this.$nextTick(() => {
				const field = this.$el.querySelector('[data-invalid]')
				if (!field) return
				field.scrollIntoView({ behavior: 'smooth', block: 'center' })
				field.querySelector('input, select, textarea')?.focus({ preventScroll: true })
			})
		},
		async onSubmit() {
			this.submitted = true
			this.serverErrors = {}
			if (!this.model.rows.length) this.addRow()
			if (this.hasClientErrors) {
				this.focusFirstError()
				return
			}
			const formData = convertObjectToFormData(this.payload())
			const isEdit = this.statusPage === 'edit'
			if (isEdit) formData.append('_method', 'PUT')
			this.togglePreloader(true)
			try {
				const { data } = await apiSecond.post(
					isEdit ? `${this.apiPrefix}/${this.$route.params.id}` : this.apiPrefix,
					formData,
				)
				this.$toast.success(this.$t(isEdit ? 'toast.saved' : 'toast.created'))
				this.$router.push(`${this.listPath}/${data.data.id}`)
			} catch (error) {
				this.checkError(error)
			} finally {
				this.togglePreloader(false)
			}
		},
		startSign(action) {
			this.signAction = action
			this.$modal.show('AquaSignModal')
		},
		requestSignature() {
			if (this.$configFile.active) return this.sendSignature(this.$configFile.xml)
			const websocket = new WebSocket('wss://127.0.0.1:13579/')
			websocket.onopen = () => {
				websocket.send(
					JSON.stringify({
						module: 'kz.gov.pki.knca.commonUtils',
						method: 'signXml',
						args: ['PKCS12', 'SIGNATURE', '<login><sessionid>aqua-report</sessionid></login>', '', ''],
					}),
				)
			}
			websocket.onclose = (event) => {
				if (!event.wasClean) alertNcaNotRunning()
			}
			websocket.onmessage = (event) => {
				const response = JSON.parse(event.data)
				if (response.responseObject) {
					websocket.close()
					this.sendSignature(response.responseObject)
				}
			}
		},
		async sendSignature(xml) {
			const formData = new FormData()
			formData.append('xml', xml)
			const path = this.signAction === 'accept' ? 'accept' : 'sign'
			this.togglePreloader(true)
			try {
				await apiSecond.post(`${this.apiPrefix}/${this.$route.params.id}/${path}`, formData)
				this.$modal.hide('AquaSignModal')
				this.$toast.success(this.$t(path === 'accept' ? 'aqua.messages.accepted' : 'aqua.messages.sent'))
				this.$router.push(this.listPath)
			} catch (error) {
				this.checkError(error)
			} finally {
				this.togglePreloader(false)
			}
		},
		async sendReturn() {
			if (!this.returnComment.trim()) return
			this.togglePreloader(true)
			try {
				await apiSecond.post(`${this.apiPrefix}/${this.$route.params.id}/return`, {
					comment: this.returnComment.trim(),
				})
				this.$modal.hide('AquaReturnModal')
				this.$toast.success(this.$t('aqua.messages.returned'))
				this.$router.push(this.listPath)
			} catch (error) {
				this.checkError(error)
			} finally {
				this.togglePreloader(false)
			}
		},
		async download(type) {
			try {
				const response = await apiSecond.get(`${this.apiPrefix}/${this.$route.params.id}/${type}`, {
					responseType: 'blob',
				})
				loadFile(response.data, response.headers['content-type'], `${this.form}-aqua-${this.$route.params.id}`)
			} catch (error) {
				this.checkError(error)
			}
		},
		togglePreloader(active) {
			document.querySelector('.preloader')?.classList.toggle('active', active)
		},
		checkError(error) {
			const status = error?.response?.status
			if (status === 401) {
				this.$router.push(`/${this.$i18n.locale}/login`)
				return
			}
			if (status === 422 && error.response.data?.errors) {
				this.serverErrors = error.response.data.errors
			}
			this.errorMessage.status = status >= 500 ? 500 : 422
			this.errorMessage.text =
				status >= 500 ? this.$t('system_message.500') : error?.response?.data?.message || this.$t('system_message.500')
			this.$modal.show('ReportManageErrorModal')
		},
	},
	watch: {
		'$route.fullPath'() {
			this.report = null
			this.loadError = null
			this.autofill = []
			this.model = this.emptyModel()
			this.document = null
			this.submitted = false
			this.serverErrors = {}
			this.loadReport()
		},
	},
	beforeCreate() {
		if (!localStorage.token) this.$router.push('/ru/login')
	},
	beforeMount() {
		this.loadReport()
		if (!(this.$store.getters._getFishes || []).length) this.$store.dispatch('_fetchFishes')
	},
}
</script>

<template>
	<div class="wrap__content aq-page">
		<div class="container">
			<div class="row">
				<div class="col-xl-2 col-lg-3">
					<v-sidebar :active="[scope === 'go' ? 'ReportingAnalyticManagement' : 'ReportingManagement']"></v-sidebar>
				</div>
				<div class="col-xl-10 col-lg-9">
					<div class="content-wrapper">
						<div class="card__content aq-shell">
							<AqPageHeader
								:title="headerTitle"
								:eyebrow="`${form}-аква`"
								:subtitle="$t(`aqua.forms.${form}`)"
								:back="listPath"
								:back-label="$t('aqua.ui.to_list')"
							>
								<template v-if="report" #meta>
									<AqStatus :value="statusValue" :label="statusLabel" />
									<AqStatus v-if="report.is_late" tone="danger" :label="$t('aqua.late')" />
									<span v-if="report.subject && scope !== 'subject'" class="aq-meta">
										{{ report.subject.name }} · {{ report.subject.iin_bin }}
									</span>
									<span v-if="report.region" class="aq-meta">{{ report.region.title }}</span>
								</template>
								<template v-if="report" #actions>
									<button type="button" class="aq-btn" @click="download('pdf')">
										<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
											<path d="M12 4v11M7 10l5 5 5-5M5 20h14" />
										</svg>
										PDF
									</button>
								</template>
							</AqPageHeader>

							<div v-if="loadError" class="aq-card">
								<AqEmpty tone="danger" :title="$t(`aqua.load_error.${loadError}`)" :text="$t('aqua.load_error.hint')">
									<router-link :to="listPath" class="aq-btn aq-btn--primary">{{ $t('aqua.load_error.back') }}</router-link>
								</AqEmpty>
							</div>

							<div v-else class="aq-layout">
								<form id="aq-report-form" class="aq-layout__main" novalidate @submit.prevent="onSubmit">
									<div v-if="report && report.return_comment && statusValue === 'canceled_by_tir'" class="aq-alert aq-alert--warning">
										<svg class="aq-alert__icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
											<path d="M4 5h16v11H8l-4 4z" />
										</svg>
										<div>
											<strong>{{ $t('aqua.return_comment') }}</strong>
											<p class="aq-alert__text">{{ report.return_comment }}</p>
										</div>
									</div>

									<div v-if="autofill.length && !isReadonly" class="aq-alert aq-alert--success">
										<svg class="aq-alert__icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
											<path d="M12 3l1.8 4.6L18 9l-4.2 1.4L12 15l-1.8-4.6L6 9l4.2-1.4z" />
											<path d="M18 15l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z" />
										</svg>
										<div>
											<strong>{{ $t('aqua.autofill.title') }}</strong>
											<ul class="aq-sources">
												<li v-for="source in autofill" :key="source.key">{{ $t(`aqua.autofill.${source.key}`, source.params) }}</li>
											</ul>
											<p class="aq-alert__text aq-alert__text--muted">{{ $t('aqua.autofill.hint') }}</p>
										</div>
									</div>

									<section id="sec-general" class="aq-card">
										<div class="aq-card__head">
											<span class="aq-step">1</span>
											<div>
												<h2 class="aq-card__title">{{ $t('aqua.ui.general') }}</h2>
												<p class="aq-card__subtitle">{{ $t('aqua.ui.general_hint') }}</p>
											</div>
										</div>
										<div class="aq-card__body aq-grid">
											<AqField
												:label="$t('inputs.region')"
												:required="!isReadonly"
												:invalid="hasError('region_id', headerErrors.region_id)"
											>
												<div v-if="isReadonly" class="aq-value">{{ report && report.region ? report.region.title : '—' }}</div>
												<region
													v-else
													class="aq-legacy"
													:required="true"
													:modelValue="model.region_id"
													:confinesValue="scope === 'subject' ? userRegions : []"
													:placeholder="$t('placeholder.choose')"
													@update:modelValue="model.region_id = $event"
												></region>
											</AqField>
											<AqField :label="$t('aqua.reporting.year')" :required="!isReadonly">
												<div v-if="isReadonly" class="aq-value">{{ model.report_year }}</div>
												<AquaSelect v-else v-model="model.report_year" :options="yearOptions" />
											</AqField>
											<AqField
												:label="fieldLabel('oked_main')"
												:required="!isReadonly"
												:invalid="hasError('oked_main', headerErrors.oked_main)"
												:autofilled="isAutofilled('oked_main')"
											>
												<div v-if="isReadonly" class="aq-value">{{ displayValue(model.oked_main) }}</div>
												<input v-else v-model.trim="model.oked_main" class="aq-input" inputmode="numeric" maxlength="10" />
											</AqField>
											<AqField :label="fieldLabel('oked_secondary')" :autofilled="isAutofilled('oked_secondary')">
												<div v-if="isReadonly" class="aq-value">{{ displayValue(model.oked_secondary) }}</div>
												<input v-else v-model.trim="model.oked_secondary" class="aq-input" inputmode="numeric" maxlength="10" />
											</AqField>
											<AqField
												v-if="form === 1"
												wide
												:label="fieldLabel('bank_account')"
												:required="!isReadonly"
												:invalid="hasError('bank_account', headerErrors.bank_account)"
												:autofilled="isAutofilled('bank_account')"
											>
												<div v-if="isReadonly" class="aq-value">{{ displayValue(model.bank_account) }}</div>
												<input v-else v-model.trim="model.bank_account" class="aq-input" />
											</AqField>
											<AqField v-if="form === 4" wide :label="fieldLabel('document')" :invalid="hasError('document')" :hint="$t('aqua.ui.document_hint')">
												<label v-if="!isReadonly" class="aq-drop">
													<input type="file" accept=".pdf,.jpg,.jpeg,.png" class="aq-drop__input" @change="onDocumentChange" />
													<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
														<path d="M12 16V4M7 9l5-5 5 5M5 20h14" />
													</svg>
													<span>{{ document ? document.name : $t('aqua.ui.choose_file') }}</span>
												</label>
												<button
													v-if="report && report.has_document"
													type="button"
													class="aq-btn aq-btn--ghost aq-btn--inline"
													@click="download('document')"
												>
													{{ $t('button.download') }}
												</button>
											</AqField>
										</div>
									</section>

									<section id="sec-rows" class="aq-card">
										<div class="aq-card__head">
											<span class="aq-step">2</span>
											<div>
												<h2 class="aq-card__title">{{ $t('aqua.ui.rows_title') }}</h2>
												<p class="aq-card__subtitle">{{ $t('aqua.ui.rows_count', { count: model.rows.length }) }}</p>
											</div>
										</div>
										<div class="aq-card__body">
											<article
												v-for="(row, rowIndex) in model.rows"
												:key="row._key"
												class="aq-row"
												:class="{ 'is-collapsed': isCollapsed(row) }"
											>
												<header class="aq-row__head">
													<button
														type="button"
														class="aq-row__toggle"
														:aria-expanded="String(!isCollapsed(row))"
														@click="toggleRow(row)"
													>
														<span class="aq-row__num">{{ rowIndex + 1 }}</span>
														<span class="aq-row__title">
															{{ rowTitle(row, rowIndex) }}
															<small v-if="rowSubtitle(row)">{{ rowSubtitle(row) }}</small>
														</span>
														<svg class="aq-row__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
															<path d="M6 9l6 6 6-6" />
														</svg>
													</button>
													<template v-if="!isReadonly">
														<span
															class="aq-badge"
															:class="rowProgress(row).filled === rowProgress(row).total ? 'aq-badge--success' : 'aq-badge--neutral'"
														>
															{{ rowProgress(row).filled }}/{{ rowProgress(row).total }}
														</span>
														<button
															v-if="model.rows.length > 1"
															type="button"
															class="aq-btn aq-btn--danger aq-btn--icon"
															:aria-label="$t('aqua.ui.remove_row')"
															:title="$t('aqua.ui.remove_row')"
															@click="removeRow(rowIndex)"
														>
															<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
																<path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" />
															</svg>
														</button>
													</template>
													<template v-else-if="report && report.rows[rowIndex]">
														<template v-for="key in percentKeys">
															<span v-if="report.rows[rowIndex][key] !== undefined" :key="key" class="aq-badge aq-badge--neutral">
																{{ $t(`aqua.percents.${key}`) }}:
																{{ report.rows[rowIndex][key] === null ? '—' : `${report.rows[rowIndex][key]} %` }}
															</span>
														</template>
													</template>
												</header>

												<div v-show="!isCollapsed(row)" class="aq-row__body">
													<div v-for="group in groupedFields(row)" :key="group.key || 'rest'" class="aq-group">
														<h3 v-if="group.key" class="aq-group__title">{{ $t(`aqua.groups.${group.key}`) }}</h3>
														<div class="aq-grid">
															<template v-for="field in group.fields">
																<AqField
																	v-if="field.type === 'obligations'"
																	:key="field.key"
																	wide
																	:label="fieldLabel('obligations')"
																	:required="!isReadonly"
																	:invalid="hasError(`rows.${rowIndex}.obligations`, rowErrorList[rowIndex].obligations)"
																>
																	<ol class="aq-obligations">
																		<li v-for="(status, index) in row.obligations" :key="index" class="aq-obligations__item">
																			<span class="aq-obligations__name">{{ obligationLabel(row, index) }}</span>
																			<span v-if="isReadonly" class="aq-value aq-value--inline">
																				{{ status ? $t(`aqua.obligation_status.${status}`) : '—' }}
																			</span>
																			<AquaSelect
																				v-else
																				v-model="row.obligations[index]"
																				class="aq-obligations__select"
																				:options="[
																					{ value: null, text: $t('placeholder.choose') },
																					...obligationStatuses.map((item) => ({ value: item, text: $t(`aqua.obligation_status.${item}`) })),
																				]"
																			/>
																		</li>
																	</ol>
																</AqField>
																<AqField
																	v-else
																	:key="field.key"
																	:wide="field.type === 'textarea'"
																	:label="fieldLabel(field.type === 'chapter' ? 'chapter' : field.key)"
																	:required="field.required && !isReadonly"
																	:invalid="hasError(`rows.${rowIndex}.${field.key}`, rowErrorList[rowIndex][field.key])"
																>
																	<div v-if="isReadonly" class="aq-value">
																		<template v-if="field.type === 'chapter'">{{ $t(`aqua.chapters.${row.chapter}`) }}</template>
																		<template v-else-if="field.type === 'fish'">{{ fishTitles[row.fish_id] || '—' }}</template>
																		<template v-else>{{ displayValue(row[field.key]) }}</template>
																	</div>
																	<AquaSelect
																		v-else-if="field.type === 'chapter'"
																		:value="row.chapter"
																		:options="chapters[form].map((item) => ({ value: item, text: $t(`aqua.chapters.${item}`) }))"
																		@change="onChapterChange(row, $event)"
																	/>
																	<fish
																		v-else-if="field.type === 'fish'"
																		class="aq-legacy"
																		:required="true"
																		:modelValue="row.fish_id"
																		:placeholder="$t('placeholder.choose')"
																		@update:modelValue="row.fish_id = $event"
																	></fish>
																	<textarea
																		v-else-if="field.type === 'textarea'"
																		v-model="row[field.key]"
																		class="aq-input aq-input--area"
																		rows="3"
																	></textarea>
																	<AquaObjectInput
																		v-else-if="objectKeys.includes(field.key)"
																		:options="objectOptions"
																		:modelValue="row[field.key]"
																		@update:modelValue="row[field.key] = $event"
																	/>
																	<input
																		v-else-if="field.type === 'text'"
																		v-model="row[field.key]"
																		class="aq-input"
																		type="text"
																	/>
																	<input
																		v-else
																		v-model="row[field.key]"
																		class="aq-input aq-input--num"
																		type="number"
																		min="0"
																		:step="field.type === 'number' ? 0.01 : 1"
																		:inputmode="field.type === 'number' ? 'decimal' : 'numeric'"
																	/>
																</AqField>
															</template>
														</div>
													</div>
												</div>
											</article>

											<button v-if="!isReadonly" type="button" class="aq-add" @click="addRow">
												<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
													<path d="M12 5v14M5 12h14" />
												</svg>
												{{ $t('aqua.add_row') }}
											</button>
										</div>
									</section>

									<section id="sec-contacts" class="aq-card">
										<div class="aq-card__head">
											<span class="aq-step">3</span>
											<div>
												<h2 class="aq-card__title">{{ $t('aqua.ui.contacts') }}</h2>
												<p class="aq-card__subtitle">{{ $t('aqua.ui.contacts_hint') }}</p>
											</div>
										</div>
										<div class="aq-card__body aq-grid">
											<AqField
												v-for="key in ['address', 'phone', 'email', 'executor_name', 'head_full_name']"
												:key="key"
												:wide="key === 'address'"
												:label="fieldLabel(key)"
												:required="!isReadonly"
												:invalid="hasError(key, headerErrors[key])"
												:autofilled="isAutofilled(key)"
											>
												<div v-if="isReadonly" class="aq-value">{{ displayValue(model[key]) }}</div>
												<input
													v-else
													v-model.trim="model[key]"
													class="aq-input"
													:type="key === 'email' ? 'email' : key === 'phone' ? 'tel' : 'text'"
													:autocomplete="key === 'email' ? 'email' : key === 'phone' ? 'tel' : 'off'"
												/>
											</AqField>
										</div>
									</section>
								</form>

								<aside class="aq-layout__side">
									<div class="aq-card aq-panel">
										<template v-if="!isReadonly">
											<div class="aq-panel__label">{{ $t('aqua.ui.progress', completion) }}</div>
											<div class="aq-panel__bar" role="progressbar" :aria-valuenow="completionPercent" aria-valuemin="0" aria-valuemax="100">
												<span :style="{ width: `${completionPercent}%` }" :class="{ 'is-done': completionPercent === 100 }"></span>
											</div>
											<div v-if="errorCount" class="aq-panel__errors" role="alert">
												<span>{{ $t('aqua.ui.errors', { count: errorCount }) }}</span>
												<button type="button" class="aq-btn aq-btn--ghost aq-btn--inline" @click="focusFirstError">
													{{ $t('aqua.ui.go_to_error') }}
												</button>
											</div>
										</template>

										<div v-if="isReadonly && percentTotals.length" class="aq-panel__kpis">
											<div class="aq-panel__label">{{ $t('aqua.plan_execution') }}</div>
											<AqKpi
												v-for="item in percentTotals"
												:key="item.key"
												:label="$t(`aqua.percents.${item.key}`)"
												:value="item.value === null ? '—' : item.value"
												:unit="item.value === null ? '' : '%'"
												:progress="item.value"
												:tone="item.value === null ? 'neutral' : item.value >= 100 ? 'success' : item.value >= 80 ? 'neutral' : 'warning'"
											/>
										</div>

										<nav class="aq-panel__nav" :aria-label="$t('aqua.ui.sections')">
											<button type="button" @click="scrollToSection('sec-general')">
												<span class="aq-step aq-step--sm">1</span>{{ $t('aqua.ui.general') }}
											</button>
											<button type="button" @click="scrollToSection('sec-rows')">
												<span class="aq-step aq-step--sm">2</span>{{ $t('aqua.ui.rows_title') }}
												<span class="aq-panel__count">{{ model.rows.length }}</span>
											</button>
											<button type="button" @click="scrollToSection('sec-contacts')">
												<span class="aq-step aq-step--sm">3</span>{{ $t('aqua.ui.contacts') }}
											</button>
										</nav>

										<div class="aq-panel__actions">
											<template v-if="!isReadonly">
												<button type="submit" form="aq-report-form" class="aq-btn aq-btn--primary aq-btn--block">
													{{ $t('button.save') }}
												</button>
												<router-link :to="statusPage === 'edit' ? `${listPath}/${$route.params.id}` : listPath" class="aq-btn aq-btn--block">
													{{ $t('button.cancel') }}
												</router-link>
											</template>
											<template v-if="canSign">
												<button type="button" class="aq-btn aq-btn--primary aq-btn--block" @click="startSign('sign')">
													{{ $t('aqua.sign_and_send') }}
												</button>
												<router-link :to="`${listPath}/${$route.params.id}/edit`" class="aq-btn aq-btn--block">
													{{ $t('button.edit') }}
												</router-link>
											</template>
											<template v-if="canReview">
												<button type="button" class="aq-btn aq-btn--primary aq-btn--block" @click="startSign('accept')">
													{{ $t('aqua.accept') }}
												</button>
												<button type="button" class="aq-btn aq-btn--block" @click="$modal.show('AquaReturnModal')">
													{{ $t('aqua.return') }}
												</button>
											</template>
										</div>
									</div>
								</aside>
							</div>

							<ReportManageErrorModal :message="message" :errorMessage="errorMessage" />
							<ReportManageModal name="AquaSignModal" @accept="requestSignature" />

							<modal class="modal__block modal__block--middle" name="AquaReturnModal" :width="560" :min-width="320" height="auto">
								<div class="aq-modal">
									<h2 class="aq-card__title">{{ $t('aqua.return_title') }}</h2>
									<p class="aq-card__subtitle">{{ $t('aqua.ui.return_hint') }}</p>
									<textarea v-model="returnComment" class="aq-input aq-input--area" rows="5" maxlength="1000"></textarea>
									<div class="aq-modal__actions">
										<button type="button" class="aq-btn" @click="$modal.hide('AquaReturnModal')">{{ $t('button.cancel') }}</button>
										<button type="button" class="aq-btn aq-btn--primary" :disabled="!returnComment.trim()" @click="sendReturn">
											{{ $t('aqua.return') }}
										</button>
									</div>
								</div>
							</modal>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.aq-shell {
	background: var(--aq-bg);
}
.aq-meta {
	color: var(--aq-muted);
	font-size: 14px;
}
.aq-layout {
	display: grid;
	grid-template-columns: minmax(0, 1fr) 300px;
	align-items: start;
	gap: 24px;
}
.aq-layout__side {
	position: sticky;
	top: 16px;
}
.aq-alert__icon {
	flex-shrink: 0;
	margin-top: 1px;
}
.aq-alert--warning .aq-alert__icon {
	color: var(--aq-warning);
}
.aq-alert--success .aq-alert__icon {
	color: var(--aq-success);
}
.aq-alert__text {
	margin: 4px 0 0;
	line-height: 1.5;
}
.aq-alert__text--muted {
	color: var(--aq-muted);
	font-size: 13px;
}
.aq-sources {
	margin: 6px 0 0;
	padding-left: 18px;
	font-size: 14px;
	line-height: 1.6;
}
.aq-step {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 32px;
	height: 32px;
	border-radius: 10px;
	background: var(--aq-primary-soft);
	color: var(--aq-primary);
	font-weight: 700;
}
.aq-step--sm {
	width: 24px;
	height: 24px;
	margin-right: 10px;
	border-radius: 8px;
	font-size: 13px;
}
.aq-value {
	min-height: 44px;
	padding: 11px 0;
	border-bottom: 1px dashed var(--aq-border-strong);
	color: var(--aq-ink);
	font-size: 15px;
	font-weight: 500;
	word-break: break-word;
}
.aq-value--inline {
	min-height: 0;
	padding: 0;
	border: 0;
	white-space: nowrap;
}
.aq-input--area {
	min-height: 96px;
	resize: vertical;
	line-height: 1.5;
}
.aq-input--num {
	font-variant-numeric: tabular-nums;
}
.aq-legacy ::v-deep .multiselect__tags {
	min-height: 44px;
	padding-top: 10px;
	border: 1px solid var(--aq-border-strong);
	border-radius: 10px;
}
.aq-legacy ::v-deep .multiselect__tags:hover {
	border-color: var(--aq-primary);
}
.aq-legacy ::v-deep .multiselect__single,
.aq-legacy ::v-deep .multiselect__placeholder {
	overflow: hidden;
	font-size: 15px;
	white-space: nowrap;
	text-overflow: ellipsis;
}
.aq-drop {
	position: relative;
	display: flex;
	align-items: center;
	gap: 10px;
	min-height: 56px;
	padding: 12px 16px;
	border: 1.5px dashed var(--aq-border-strong);
	border-radius: var(--aq-radius);
	color: var(--aq-ink-2);
	cursor: pointer;
	transition: border-color 0.2s var(--aq-ease), background 0.2s var(--aq-ease);
}
.aq-drop:hover {
	border-color: var(--aq-primary);
	background: var(--aq-primary-soft);
}
.aq-drop__input {
	position: absolute;
	inset: 0;
	opacity: 0;
	cursor: pointer;
}
.aq-btn--inline {
	align-self: flex-start;
	min-height: 36px;
	padding: 6px 12px;
}
.aq-btn--icon {
	min-height: 40px;
	width: 40px;
	padding: 0;
}
.aq-row {
	margin-bottom: 14px;
	border: 1px solid var(--aq-border);
	border-radius: var(--aq-radius);
	background: var(--aq-surface);
	transition: box-shadow 0.2s var(--aq-ease), border-color 0.2s var(--aq-ease);
}
.aq-row:focus-within {
	border-color: #b9d6f8;
	box-shadow: var(--aq-shadow-hover);
}
.aq-row__head {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 10px;
	padding: 10px 14px 10px 10px;
	border-bottom: 1px solid var(--aq-border);
}
.aq-row.is-collapsed .aq-row__head {
	border-bottom-color: transparent;
}
.aq-row__toggle {
	display: flex;
	flex: 1 1 280px;
	align-items: center;
	gap: 12px;
	min-height: 44px;
	padding: 4px 6px;
	border-radius: 10px;
	text-align: left;
}
.aq-row__toggle:hover {
	background: var(--aq-bg);
}
.aq-row__toggle:focus-visible {
	outline: none;
	box-shadow: 0 0 0 3px rgba(21, 127, 238, 0.25);
}
.aq-row__num {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 30px;
	height: 30px;
	border-radius: 50%;
	background: var(--aq-ink);
	color: #fff;
	font-size: 13px;
	font-weight: 700;
}
.aq-row__title {
	display: flex;
	flex: 1;
	flex-direction: column;
	font-weight: 600;
	color: var(--aq-ink);
}
.aq-row__title small {
	margin-top: 2px;
	color: var(--aq-muted);
	font-size: 13px;
	font-weight: 400;
}
.aq-row__body .aq-grid {
	grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
	align-items: end;
}
.aq-row__chevron {
	color: var(--aq-muted);
	transition: transform 0.2s var(--aq-ease);
}
.aq-row.is-collapsed .aq-row__chevron {
	transform: rotate(-90deg);
}
.aq-row__body {
	padding: 8px 20px 20px;
}
.aq-group {
	margin-top: 16px;
}
.aq-group__title {
	margin: 0 0 12px;
	color: var(--aq-primary);
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 0.06em;
	text-transform: uppercase;
}
.aq-obligations {
	margin: 0;
	padding: 0;
	list-style: none;
	counter-reset: obligation;
}
.aq-obligations__item {
	display: grid;
	grid-template-columns: minmax(0, 1fr) 220px;
	align-items: center;
	gap: 12px;
	padding: 10px 0;
	border-bottom: 1px solid var(--aq-border);
	counter-increment: obligation;
}
.aq-obligations__name::before {
	content: counter(obligation) '. ';
	color: var(--aq-muted);
	font-weight: 600;
}
.aq-obligations__name {
	line-height: 1.45;
}
.aq-add {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	width: 100%;
	min-height: 52px;
	border: 1.5px dashed var(--aq-border-strong);
	border-radius: var(--aq-radius);
	color: var(--aq-primary);
	font-weight: 600;
	transition: background 0.2s var(--aq-ease), border-color 0.2s var(--aq-ease);
}
.aq-add:hover {
	border-color: var(--aq-primary);
	background: var(--aq-primary-soft);
}
.aq-panel {
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 20px;
}
.aq-panel__label {
	color: var(--aq-ink-2);
	font-size: 14px;
	font-weight: 600;
}
.aq-panel__bar {
	height: 8px;
	margin-top: -8px;
	border-radius: 999px;
	background: #edf2f8;
	overflow: hidden;
}
.aq-panel__bar span {
	display: block;
	height: 100%;
	border-radius: inherit;
	background: var(--aq-primary);
	transition: width 0.3s var(--aq-ease);
}
.aq-panel__bar span.is-done {
	background: var(--aq-success);
}
.aq-panel__errors {
	display: flex;
	flex-direction: column;
	gap: 6px;
	padding: 12px;
	border-radius: 10px;
	background: var(--aq-danger-soft);
	color: var(--aq-danger);
	font-size: 14px;
	font-weight: 500;
}
.aq-panel__kpis {
	display: flex;
	flex-direction: column;
	gap: 10px;
}
.aq-panel__kpis ::v-deep .aq-kpi {
	padding: 12px 14px;
	box-shadow: none;
}
.aq-panel__kpis ::v-deep .aq-kpi__value {
	font-size: 22px;
}
.aq-panel__nav {
	display: flex;
	flex-direction: column;
	gap: 2px;
	padding-top: 12px;
	border-top: 1px solid var(--aq-border);
}
.aq-panel__nav button {
	display: flex;
	align-items: center;
	min-height: 40px;
	padding: 6px 8px;
	border-radius: 8px;
	color: var(--aq-ink);
	font-size: 14px;
	text-align: left;
}
.aq-panel__nav button:hover {
	background: var(--aq-bg);
	color: var(--aq-primary);
}
.aq-panel__count {
	margin-left: auto;
	color: var(--aq-muted);
	font-size: 13px;
}
.aq-panel__actions {
	display: flex;
	flex-direction: column;
	gap: 10px;
	padding-top: 12px;
	border-top: 1px solid var(--aq-border);
}
.aq-modal {
	display: flex;
	flex-direction: column;
	gap: 14px;
	padding: 28px;
}
.aq-modal__actions {
	display: flex;
	justify-content: flex-end;
	gap: 10px;
}
@media (max-width: 1280px) {
	.aq-layout {
		grid-template-columns: minmax(0, 1fr);
	}
	.aq-layout__side {
		position: static;
		order: -1;
	}
	.aq-panel__nav {
		display: none;
	}
	.aq-panel__actions {
		flex-direction: row;
		flex-wrap: wrap;
	}
	.aq-panel__actions .aq-btn--block {
		width: auto;
		flex: 1 1 180px;
	}
}
@media (max-width: 768px) {
	.aq-card__body {
		padding: 16px;
	}
	.aq-obligations__item {
		grid-template-columns: minmax(0, 1fr);
	}
}
</style>
